import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchUsersWithFirebaseId as fetchUsersWithFirebaseIdApi,
  sendPushNotification as sendPushNotificationApi,
} from "../../services/api";

export type PushFirebaseUser = {
  id: number;
  email?: string;
  first_name?: string | null;
  last_name?: string | null;
  esim_count?: number;
  firebase_id?: string | null;
};

export type PushSendPayload = {
  user_ids: number[];
  title: string;
  body: string;
  data?: Record<string, unknown>;
};

export type PushSendResult = {
  sent?: number;
  skipped?: number;
  [key: string]: unknown;
};

interface PushState {
  firebaseUsers: PushFirebaseUser[];
  loading: "idle" | "pending" | "succeeded" | "failed";
  sending: "idle" | "pending" | "succeeded" | "failed";
  lastResult: PushSendResult | null;
  error: string | null;
}

const initialState: PushState = {
  firebaseUsers: [],
  loading: "idle",
  sending: "idle",
  lastResult: null,
  error: null,
};

export const fetchFirebaseUsers = createAsyncThunk(
  "push/fetchFirebaseUsers",
  async () => fetchUsersWithFirebaseIdApi(),
);

export const sendNotification = createAsyncThunk(
  "push/sendNotification",
  async (payload: PushSendPayload) => sendPushNotificationApi(payload),
);

const pushSlice = createSlice({
  name: "push",
  initialState,
  reducers: {
    clearResult(state) {
      state.lastResult = null;
      state.sending = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFirebaseUsers.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(fetchFirebaseUsers.fulfilled, (state, action) => {
        state.loading = "succeeded";
        const payload: any = action.payload;
        if (Array.isArray(payload)) {
          state.firebaseUsers = payload;
        } else if (Array.isArray(payload?.users)) {
          state.firebaseUsers = payload.users;
        } else if (Array.isArray(payload?.data)) {
          state.firebaseUsers = payload.data;
        } else {
          state.firebaseUsers = [];
        }
      })
      .addCase(fetchFirebaseUsers.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.error.message ?? "Failed to fetch firebase users";
      })
      .addCase(sendNotification.pending, (state) => {
        state.sending = "pending";
        state.error = null;
      })
      .addCase(sendNotification.fulfilled, (state, action) => {
        state.sending = "succeeded";
        state.lastResult = action.payload as PushSendResult;
      })
      .addCase(sendNotification.rejected, (state, action) => {
        state.sending = "failed";
        state.error = action.error.message ?? "Failed to send";
      });
  },
});

export const { clearResult } = pushSlice.actions;
export default pushSlice.reducer;
